/** Transportistas como datos (sección 8): lista y editor JSON validado con CarrierSchema en el servidor. */
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, JsonEditor, muted, Notice, Table, td, str } from '../lib/ui.js';

type Doc = Record<string, unknown>;

const TEMPLATE = {
  display_name: { es: 'Nuevo transportista', en: 'New carrier', hu: 'Új fuvarozó' },
  logo_url: null,
  status: 'draft',
  adapter_type: 'manual-tracking-only',
  credentials_ref: null,
  adapter_config: {},
  corridors: [
    {
      from: 'ES',
      to: 'ES',
      service_level: 'home',
      enabled: true,
      customs_supported: false,
      sla_days: 3,
    },
  ],
  pickup: { mode: 'none', widget_provider: 'none' },
  price_matrix: {
    currency: 'EUR',
    tiers: [],
    margin: { amount: 20, currency: 'EUR' },
    surcharges: [],
  },
  label: { format: 'pdf', size: 'A6', validity_days: 7, qr_supported: false },
  tracking: { url_template: null, source: 'manual', status_map: {} },
  webhook: null,
  ux: { sort_order: 99, badge: null, excluded_category_ids: [] },
};

function strip(d: Doc): Doc {
  const { id: _i, audit: _a, created_at: _c, ...rest } = d;
  return rest;
}

export function CarriersPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [newId, setNewId] = useState('');
  const navigate = useNavigate();
  useEffect(() => {
    getDocs(collection(db, 'carriers'))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
  }, []);
  return (
    <>
      <h1 style={{ margin: 0 }}>Transportistas</h1>
      <p style={muted}>
        Capacidad = código (packeta, sendcloud, eurosender, manual-tracking-only); configuración =
        este documento. Las credenciales son NOMBRES de Secret Manager, nunca valores.
      </p>
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && (
        <Table head={['Id', 'Nombre', 'Adaptador', 'Estado', 'Corredores', 'Recogida', 'Versión']}>
          {rows.map((c) => (
            <tr key={String(c.id)}>
              <td style={td}>
                <Link to={`/transportistas/${String(c.id)}`}>{String(c.id)}</Link>
              </td>
              <td style={td}>{str((c.display_name as Doc | undefined)?.es)}</td>
              <td style={td}>{String(c.adapter_type)}</td>
              <td style={td}>{String(c.status)}</td>
              <td style={td}>{((c.corridors as unknown[] | undefined) ?? []).length}</td>
              <td style={td}>{str((c.pickup as Doc | undefined)?.mode)}</td>
              <td style={td}>{str((c.audit as Doc | undefined)?.version)}</td>
            </tr>
          ))}
        </Table>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (/^[a-z0-9_-]{2,40}$/.test(newId)) void navigate(`/transportistas/${newId}?new=1`);
        }}
        style={{ display: 'flex', gap: 8, alignItems: 'center' }}
      >
        <input
          style={{ ...field, width: 240 }}
          placeholder="id nuevo (p. ej. inpost_pl)"
          value={newId}
          onChange={(e) => {
            setNewId(e.target.value);
          }}
        />
        <button type="submit" style={btn(false)}>
          Crear transportista
        </button>
      </form>
    </>
  );
}

export function CarrierPage(): ReactElement {
  const { id = '' } = useParams();
  const [text, setText] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getDoc(doc(db, 'carriers', id))
      .then((s) => {
        setText(
          JSON.stringify(
            s.exists() ? strip(s.data()) : TEMPLATE,
            (_k, v: unknown) =>
              v && typeof v === 'object' && 'toDate' in v
                ? (v as { toDate: () => Date }).toDate().toISOString()
                : v,
            2,
          ),
        );
      })
      .catch(() => {
        setText(JSON.stringify(TEMPLATE, null, 2));
      });
  }, [id]);
  const save = (): void => {
    setError(null);
    let data: Doc;
    try {
      data = JSON.parse(text ?? '') as Doc;
    } catch (e) {
      setError(`JSON no válido: ${String(e)}`);
      return;
    }
    call(api.admin.updateCarrier, { id, data, ...(reason ? { reason } : {}) })
      .then(() => {
        setNotice('Guardado.');
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  if (text === null) return <p>Cargando…</p>;
  return (
    <>
      <h1 style={{ margin: 0 }}>
        Transportista <code>{id}</code>
      </h1>
      <Notice text={notice} />
      <Notice text={error} error />
      <section style={card}>
        <JsonEditor value={text} onChange={setText} rows={30} />
        <input
          style={field}
          placeholder="Motivo del cambio (auditLog)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={btn(true)} onClick={save}>
            Guardar (valida en el servidor)
          </button>
          <Link to="/transportistas" style={btn(false)}>
            Volver
          </Link>
        </div>
        <p style={muted}>
          `status`: draft (no se ofrece) · shadow (solo lo ven admins) · active · paused · retired.
          Un corredor sin `enabled` no cotiza.
        </p>
      </section>
    </>
  );
}
